import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { DEFAULT_CANVAS_TEXT_SIZE } from './utils/canvasText'
import { getCanvasNoteFont } from './utils/canvasFonts'
import {
  getCanvasNoteColorHex,
  DEFAULT_CANVAS_NOTE_COLOR,
} from './utils/canvasNoteStyle'
import { getMemoColor } from './utils/canvasMemoStyle'
import { getPlacementSize } from './utils/canvasPlacement'
import { getAreaColor } from './utils/canvasArea'
import './CanvasPlacementPreview.scss'

export interface CanvasPlacementPreviewProps {
  /**
   * Die MITTE des Bildes in Client-Koordinaten; `null` blendet es aus.
   *
   * Fertig gerechnet übergeben, nicht die rohe Zeigerstelle: Über der Fläche
   * zeigt das Bild die eingerastete Lage — man sieht vorher, wo die Notiz
   * landet.
   */
  point: { x: number; y: number } | null
  /** Die Zoomstufe des Canvas — das Bild zeigt die spätere Größe. */
  zoom: number
  /**
   * Welche Art gleich abgesetzt wird. Das Bild zeigt sie — sonst sähe eine
   * Memo beim Absetzen aus wie eine Notiz und wäre dann plötzlich etwas
   * anderes.
   *
   * **Der Bereich ist seit 2026-09-06 dabei.** Er hängt allerdings nur am
   * ZUG vom Werkzeug-Knopf; wer das Werkzeug anklickt, bekommt weiterhin das
   * Fadenkreuz zum **Aufziehen** (die Unterscheidung trifft `CanvasFlow`).
   */
  kind: 'note' | 'memo' | 'area' | 'text'
  /**
   * Beim **Text**: die Schriftgröße und Schrift, in der er gleich entsteht
   * (zuletzt gewählt). Das Bild ist dann der graue Platzhalter „Text
   * hinzufügen" — und `point` seine linke obere Ecke, nicht die Mitte.
   */
  fontSize?: number
  fontKey?: string | null
  /**
   * Die Farbe, in der der Zettel gleich landet.
   *
   * Muss von außen kommen und darf hier nicht die Vorgabe sein: Ein neuer
   * Zettel erbt Farbe und Schrift des zuletzt eingestellten (**Zuletzt gewählt
   * gilt weiter**, CONTEXT.md). Zeigte das Bild stattdessen immer Gelb, wäre es
   * genau in dem Moment falsch, in dem man sich auf es verlässt — man sähe Gelb
   * und bekäme, was man zuletzt eingestellt hat.
   */
  colorKey?: string | null
}


/**
 * Kantenlänge des Werkzeug-Symbols. Aus ihr wächst das Bild heraus — steht
 * hier und nicht in der SCSS, weil der Startwert gegen die (zoomabhängige)
 * Zielgröße gerechnet werden muss.
 */
const TOOLBAR_ICON_SIZE = 24

/**
 * Die Notiz, der am Zeiger hängt, während einer abgelegt wird.
 *
 * **Fest positioniert in Bildschirmkoordinaten**, nicht im `ViewportPortal`:
 * Das Bild muss schon sichtbar sein, während der Zeiger noch über der
 * Werkzeug-Pille steht — also außerhalb des Canvas, wo dessen Koordinaten
 * nicht gelten.
 *
 * **Und in einem Portal am `body`**, nicht im Baum des Canvas: `.react-flow`
 * ist ein eigener Stapelkontext (React Flow setzt `position: relative;
 * zIndex: 0` inline, siehe ADR 0008), und die Werkzeug-Pille liegt darin
 * zuoberst. Innerhalb dieses Kontexts hülfe kein noch so hoher Wert — das Bild
 * verschwände unter der Pille, aus der man es gerade zieht. Am `body`
 * konkurriert es unmittelbar mit beiden.
 *
 * Deshalb wird die Größe **gegen den Zoom gerechnet**: Bei Zoomstufe 0,5 ist
 * eine Notiz auf dem Schirm 84px breit, das Vorschaubild also auch. Sonst
 * verspränge die Größe im Moment des Ablegens.
 *
 * Es wächst aus der Größe des Werkzeug-Symbols heraus — **immer**, egal ob
 * es aus dem Knopf gezogen oder per `N` scharfgestellt wurde. Skaliert wird am
 * Ort des Zeigers, nicht vom Knopf herüber; die Bewegung funktioniert also an
 * jeder Stelle gleich, und zwei Erscheinungsweisen für dieselbe Sache
 * erklärten sich nicht.
 *
 * **Je Art ihre eigene Form**: quadratisch für die Notiz, breiter und
 * stärker gerundet für die Notiz (`--sticky`/`--text`) — das Bild zeigt, was
 * ENTSTEHT, nicht nur, dass etwas entsteht. Sonst sähe eine Notiz beim
 * Absetzen für einen Moment aus wie eine Notiz und würde erst danach zu
 * etwas anderem.
 *
 * @example
 * <CanvasPlacementPreview point={{ x: 320, y: 180 }} zoom={0.5} kind="note" />
 */
export const CanvasPlacementPreview: React.FC<CanvasPlacementPreviewProps> = ({ point, zoom, kind, colorKey, fontSize, fontKey }) => {
  const { t } = useTranslation()
  if (!point) return null

  /*
    Der **Text** ist kein Zettel: kein Papier, nur der graue Platzhalter am
    Fadenkreuz (CONTEXT.md, Text) — in der Größe, in der er gleich entsteht,
    gegen den Zoom gerechnet. Er hängt mit seiner Ecke am Zeiger, denn dort
    beginnt der Text beim Klick (`getCanvasTextPosition`).
  */
  if (kind === 'text') {
    const font = getCanvasNoteFont(fontKey)
    return createPortal(
      <div
        className="canvas-placement-preview canvas-placement-preview--text"
        style={{
          left: point.x,
          top: point.y,
          fontFamily: font.stack,
          fontWeight: font.weight,
          fontSize: (fontSize ?? DEFAULT_CANVAS_TEXT_SIZE) * font.scale * zoom,
        }}
      >
        {t('projectCanvas.text.placeholder')}
      </div>,
      document.body,
    )
  }

  /*
    Die Breite kommt aus derselben Quelle wie beim Ablegen. Genau hier lief es
    schon einmal auseinander: Das Bild zeigte eine Breite, gerastet wurde eine
    andere, und die Notiz sprang beim Loslassen zur Seite.

    Die Höhe ebenso — auch da stand einmal eine eigene Zahl (56), aus der Zeit,
    als eine leere Notiz mit dem Text wuchs. Heute hat sie `minHeight:
    MEMO_MIN_HEIGHT` und ist frisch abgesetzt so hoch wie eine Notiz; das
    Bild war dagegen ein Strich.
  */
  const { width: baseWidth, height: baseHeight } = getPlacementSize(kind)
  const width = baseWidth * zoom
  const height = baseHeight * zoom
  /*
    Füllung und Rand je Art, und zwar aus DERSELBEN Quelle, aus der das echte
    Element sie später nimmt:

    | Art | Quelle | warum |
    |---|---|---|
    | Notiz | `getCanvasNoteColorHex` | Papierfarbe, kein Rand |
    | Memo | `getMemoColor` | Fläche und Kante gehören zusammen |
    | Bereich | `getAreaColor` | dieselbe wie `.canvas-area-draw` |

    **Auch der Bereich hört auf `colorKey`** — seit er sich seine Farbe merkt
    wie Notiz und Memo (2026-09-06). Vorher stand hier fest `getAreaColor(null)`,
    weil es nichts zu merken gab; das blieb beim Nachrüsten zunächst stehen und
    zeigte den Standardton, während der abgesetzte Bereich längst anders
    herauskam. Ein Bild, das etwas anderes zeigt als sein Ergebnis, ist genau in
    dem Moment falsch, in dem man sich auf es verlässt.

    `?? null` und nicht `?? DEFAULT_CANVAS_NOTE_COLOR` wie bei den anderen:
    `getAreaColor` fällt selbst auf den Standardton zurück.
  */
  const areaColor = kind === 'area' ? getAreaColor(colorKey ?? null) : null
  const textColor = kind === 'memo' ? getMemoColor(colorKey ?? DEFAULT_CANVAS_NOTE_COLOR) : null
  const edge = areaColor ?? textColor
  const fill = edge ? edge.surface : getCanvasNoteColorHex(colorKey ?? DEFAULT_CANVAS_NOTE_COLOR)

  return createPortal(
    <div
      className={`canvas-placement-preview canvas-placement-preview--grow canvas-placement-preview--${kind}`}
      style={{
        left: point.x,
        top: point.y,
        width,
        height,
        '--preview-fill': fill,
        ...(edge ? { '--preview-border': edge.border } : {}),
        // Der **Streifen** — nur das Memo hat einen. Ohne ihn zeigte das Bild
        // am Zeiger etwas anderes als das, was gleich entsteht.
        ...(textColor ? {
          '--preview-stripe': textColor.stripe,
          '--preview-stripe-line': textColor.stripeLine,
        } : {}),
        // Gegen die ZIELgröße gerechnet, nicht fest gesetzt: So beginnt das
        // Bild bei jeder Zoomstufe genau bei der Kantenlänge des Symbols und
        // wächst von dort auf seine Größe. Ein fester Faktor stimmte nur bei
        // Zoom 1.
        '--preview-start-scale': width > 0 ? TOOLBAR_ICON_SIZE / width : 1,
      } as React.CSSProperties}
    />,
    document.body,
  )
}
